import { ProductGroup } from "../../api/entities/ProductGroup";
import { FC, useEffect, useState } from "react";
import {
  EuiButton,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiPanel,
  EuiTextArea,
  EuiFieldNumber,
  EuiCheckbox,
} from "@elastic/eui";
import ProductGroupServicesEditor from "components/products/ProductGroupServicesEditor";

export interface IProductGroupEditor {
  productGroup: ProductGroup;
  onProductGroupUpdated: (productGroup: ProductGroup) => void;
  onSaveClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export const ProductGroupEditor: FC<IProductGroupEditor> = (props) => {
  const [flagshipProgramChecked, setFlagshipProgramChecked] = useState(
    props.productGroup.isFlagshipProgram,
  );

  useEffect(() => {
    setFlagshipProgramChecked(props.productGroup.isFlagshipProgram);
  }, [props.productGroup.id]);

  const onSaveClick = () => {
    props.onSaveClick();
  };

  const onFlagshipProgramChecked = () => {
    setFlagshipProgramChecked((val) => {
      const newValue = !val;
      props.onProductGroupUpdated({
        ...props.productGroup,
        isFlagshipProgram: newValue,
      });
      return newValue;
    });
  };

  const onAddServiceClicked = () => {
    props.onProductGroupUpdated({
      ...props.productGroup,
      clientFacingProductGroupServices: [
        ...props.productGroup.clientFacingProductGroupServices,
        "",
      ],
    });
  };

  const onEditServiceText = (index: number, text: string) => {
    const services = [...props.productGroup.clientFacingProductGroupServices];
    services[index] = text;
    props.onProductGroupUpdated({
      ...props.productGroup,
      clientFacingProductGroupServices: services,
    });
  };

  const onMoveServiceUp = (index: number) => {
    const services = [...props.productGroup.clientFacingProductGroupServices];
    const service = services[index];
    services[index] = services[index - 1];
    services[index - 1] = service;
    props.onProductGroupUpdated({
      ...props.productGroup,
      clientFacingProductGroupServices: services,
    });
  };

  const onMoveServiceDown = (index: number) => {
    const services = [...props.productGroup.clientFacingProductGroupServices];
    const service = services[index];
    services[index] = services[index + 1];
    services[index + 1] = service;
    props.onProductGroupUpdated({
      ...props.productGroup,
      clientFacingProductGroupServices: services,
    });
  };

  const onRemoveService = (index: number) => {
    const services = [...props.productGroup.clientFacingProductGroupServices];
    services.splice(index, 1);
    props.onProductGroupUpdated({
      ...props.productGroup,
      clientFacingProductGroupServices: services,
    });
  };

  return (
    <EuiPanel hasBorder={true} hasShadow={false}>
      <EuiFormRow label={"Group Name"}>
        <EuiFieldText
          value={props.productGroup.name}
          onChange={(e) =>
            props.onProductGroupUpdated({
              ...props.productGroup,
              name: e.target.value,
            })
          }
        />
      </EuiFormRow>
      <EuiFormRow label={"Group Description"}>
        <EuiTextArea
          value={props.productGroup.description}
          onChange={(e) =>
            props.onProductGroupUpdated({
              ...props.productGroup,
              description: e.target.value,
            })
          }
        />
      </EuiFormRow>
      <EuiFormRow>
        <EuiCheckbox
          id="isFlagshipProgram"
          onChange={onFlagshipProgramChecked}
          checked={flagshipProgramChecked}
          label="Include as Flagship Program"
        />
      </EuiFormRow>
      <EuiFormRow label={"Product Price"}>
        <EuiFieldNumber
          prepend={"$"}
          append={"/ month"}
          value={props.productGroup.pricePerMonth.toString()}
          onChange={(e) =>
            props.onProductGroupUpdated({
              ...props.productGroup,
              pricePerMonth: Number(e.target.value),
            })
          }
        />
      </EuiFormRow>
      <EuiFormRow label={"Net Commissionable Revenue"}>
        <EuiFieldNumber
          prepend={"$"}
          value={props.productGroup.netCommissionableRevenue}
          onChange={(e) =>
            props.onProductGroupUpdated({
              ...props.productGroup,
              netCommissionableRevenue: parseFloat(e.target.value),
            })
          }
        />
      </EuiFormRow>
      <EuiFormRow label={"Setup Fee"}>
        <EuiFieldNumber
          prepend={"$"}
          value={props.productGroup.setupFee}
          onChange={(e) =>
            props.onProductGroupUpdated({
              ...props.productGroup,
              setupFee: parseFloat(e.target.value),
            })
          }
        />
      </EuiFormRow>
      <EuiFormRow>
        <ProductGroupServicesEditor
          services={props.productGroup.clientFacingProductGroupServices}
          onAddService={onAddServiceClicked}
          onEditServiceText={onEditServiceText}
          onMoveServiceUp={onMoveServiceUp}
          onMoveServiceDown={onMoveServiceDown}
          onRemoveService={onRemoveService}
        />
      </EuiFormRow>
      <EuiFormRow>
        <EuiFlexGroup>
          <EuiFlexItem grow={false}>
            <EuiButton
              fill
              isLoading={props.isLoading}
              isDisabled={props.isDisabled}
              onClick={onSaveClick}
            >
              Save
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFormRow>
    </EuiPanel>
  );
};
